import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Grid, List, Calendar, Search, Filter, Download, 
  Share2, Star, Heart, MessageSquare, Image as ImageIcon,
  Video, FileText, Music, Sparkles, X, ChevronLeft, ChevronRight,
  ZoomIn, Play, Pause, MoreVertical, Tag, Users, Clock
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';

const EpicLoreLibrary = ({ projectId, projectName, onClose }) => {
  const [viewMode, setViewMode] = useState('gallery'); // gallery, timeline, story, masonry
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [filter, setFilter] = useState('all'); // all, images, videos, moments, decisions
  const [searchQuery, setSearchQuery] = useState('');
  const [orbPhase, setOrbPhase] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slower Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setOrbPhase(prev => (prev + 0.1) % 360);
    }, 100);
    return () => clearInterval(spiralInterval);
  }, []);

  // Mock gallery data - in production, this comes from backend
  const artifacts = [
    {
      id: '1',
      type: 'moment',
      title: 'Project Kickoff',
      description: 'The day everything began - our family gathered around the dinner table to dream up this reunion',
      date: new Date('2024-10-01'),
      image: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=800&q=80',
      participants: ['Craig', 'Sarah', 'Mom', 'Dad', 'Alex'],
      tags: ['milestone', 'family', 'planning'],
      emotions: ['excited', 'hopeful'],
      stats: { loves: 12, comments: 8 }
    },
    {
      id: '2',
      type: 'decision',
      title: 'Venue Selection',
      description: 'After much deliberation, we chose Riverside Gardens - a place that holds memories from grandma\'s time',
      date: new Date('2024-10-05'),
      image: 'https://images.unsplash.com/photo-1519167758481-83f29da8c9a7?w=800&q=80',
      participants: ['Craig', 'Planning Committee'],
      tags: ['decision', 'venue', 'historic'],
      emotion: 'nostalgic',
      stats: { loves: 18, comments: 15 }
    },
    {
      id: '3',
      type: 'image',
      title: 'Budget Spreadsheet Art',
      description: 'Craig turned our budget into a work of art - every dollar a brushstroke of love',
      date: new Date('2024-10-08'),
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
      participants: ['Craig'],
      tags: ['budget', 'planning', 'art'],
      emotion: 'focused',
      stats: { loves: 8, comments: 3 }
    },
    {
      id: '4',
      type: 'remedy',
      title: 'The Great Catering Debate',
      description: 'Tensions rose about buffet vs. plated dinner. We held a Remedy Council and found harmony through compromise',
      date: new Date('2024-10-12'),
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800&q=80',
      participants: ['Family Council'],
      tags: ['remedy', 'harmony', 'growth'],
      emotion: 'resolved',
      stats: { loves: 25, comments: 12 },
      remedyQuote: '"We release the story. We remember the mission."'
    },
    {
      id: '5',
      type: 'moment',
      title: 'Guest List Complete',
      description: '100 souls will gather - three generations reuniting under one sky',
      date: new Date('2024-10-15'),
      image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
      participants: ['Sarah', 'Mom'],
      tags: ['milestone', 'guests', 'family'],
      emotion: 'accomplished',
      stats: { loves: 30, comments: 20 }
    },
    {
      id: '6',
      type: 'image',
      title: 'Music Selection',
      description: 'Created the perfect playlist - from grandma\'s swing era to the kids\' favorite songs',
      date: new Date('2024-10-18'),
      image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&q=80',
      participants: ['Alex', 'Cousins'],
      tags: ['music', 'culture', 'generations'],
      emotion: 'joyful',
      stats: { loves: 22, comments: 18 }
    },
    {
      id: '7',
      type: 'video',
      title: 'Virtual Tour of Venue',
      description: 'We walked through Riverside Gardens together via video call, each seeing where our story will unfold',
      date: new Date('2024-10-20'),
      image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&q=80',
      participants: ['Everyone'],
      tags: ['venue', 'preview', 'technology'],
      emotion: 'anticipation',
      stats: { loves: 35, comments: 25 },
      duration: '12:34'
    },
    {
      id: '8',
      type: 'moment',
      title: 'Menu Tasting Day',
      description: 'Five family members became food critics for a day - laughter, debates, and delicious discoveries',
      date: new Date('2024-10-22'),
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
      participants: ['Tasting Team'],
      tags: ['food', 'experience', 'decision'],
      emotion: 'delighted',
      stats: { loves: 40, comments: 30 }
    }
  ];

  const filteredArtifacts = artifacts.filter(artifact => {
    const matchesFilter = filter === 'all' || artifact.type === filter || 
      (filter === 'images' && (artifact.type === 'image' || artifact.type === 'moment'));
    const matchesSearch = searchQuery === '' || 
      artifact.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      artifact.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      artifact.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  // Auto-play slideshow
  useEffect(() => {
    if (isPlaying && viewMode === 'story') {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % filteredArtifacts.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, viewMode, filteredArtifacts.length]);

  const handleArtifactClick = (artifact) => {
    setSelectedArtifact(artifact);
  };

  const handlePrevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + filteredArtifacts.length) % filteredArtifacts.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % filteredArtifacts.length);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-amber-50 via-purple-50 to-stone-100 z-50 overflow-hidden">
      {/* Grant Spiral Background */}
      <div className="fixed inset-0 pointer-events-none opacity-10" style={{ zIndex: 0 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${orbPhase * 0.3}deg)`,
            filter: 'blur(3px) saturate(1.5) brightness(1.3)',
            mixBlendMode: 'multiply'
          }}
        >
          <GrantitudeSpiral />
        </div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-white/95 backdrop-blur-xl border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-stone-500 hover:text-stone-700 transition-colors"
              >
                <X size={24} />
              </button>
              <div className="h-6 w-px bg-stone-200"></div>
              <BookOpen className="text-purple-500" size={24} />
              <div>
                <h1 className="text-xl font-semibold text-stone-800">Lore Library</h1>
                <p className="text-xs text-stone-500">{projectName} · {artifacts.length} artifacts</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors text-stone-600" title="Download Collection">
                <Download size={18} />
              </button>
              <button className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors text-stone-600" title="Share">
                <Share2 size={18} />
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-lavender-500 hover:from-purple-600 hover:to-lavender-600 text-white rounded-lg text-sm font-medium transition-all flex items-center gap-2">
                <Sparkles size={16} />
                AI Curate
              </button>
            </div>
          </div>

          {/* View Mode Selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('gallery')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'gallery' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <Grid size={16} />
                Gallery
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'timeline' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <Calendar size={16} />
                Timeline
              </button>
              <button
                onClick={() => setViewMode('story')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'story' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <BookOpen size={16} />
                Story Mode
              </button>
              <button
                onClick={() => setViewMode('masonry')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  viewMode === 'masonry' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <List size={16} />
                Masonry
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <input
                  type="text"
                  placeholder="Search artifacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-1.5 bg-stone-100 border border-stone-200 rounded-lg text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-purple-400 w-48"
                />
              </div>
              
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-1.5 bg-stone-100 border border-stone-200 rounded-lg text-sm text-stone-700 focus:outline-none focus:border-purple-400"
              >
                <option value="all">All Types</option>
                <option value="moment">Moments</option>
                <option value="decision">Decisions</option>
                <option value="remedy">Remedies</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 h-[calc(100vh-140px)] overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {viewMode === 'gallery' && <GalleryView artifacts={filteredArtifacts} onArtifactClick={handleArtifactClick} />}
          {viewMode === 'timeline' && <TimelineView artifacts={filteredArtifacts} onArtifactClick={handleArtifactClick} />}
          {viewMode === 'story' && (
            <StoryView 
              artifacts={filteredArtifacts} 
              currentSlide={currentSlide}
              isPlaying={isPlaying}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onPrev={handlePrevSlide}
              onNext={handleNextSlide}
            />
          )}
          {viewMode === 'masonry' && <MasonryView artifacts={filteredArtifacts} onArtifactClick={handleArtifactClick} />}
        </div>
      </div>

      {/* Artifact Detail Modal */}
      {selectedArtifact && (
        <ArtifactDetailModal 
          artifact={selectedArtifact} 
          onClose={() => setSelectedArtifact(null)} 
        />
      )}
    </div>
  );
};

// Gallery View Component
const GalleryView = ({ artifacts, onArtifactClick }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {artifacts.map((artifact, index) => (
        <button
          key={artifact.id}
          onClick={() => onArtifactClick(artifact)}
          className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          {/* Image */}
          <div className="aspect-square overflow-hidden bg-stone-100">
            <img 
              src={artifact.image} 
              alt={artifact.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          </div>

          {/* Type Badge */}
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-lg text-xs font-medium backdrop-blur-md ${
              artifact.type === 'moment' ? 'bg-purple-500/80 text-white' :
              artifact.type === 'decision' ? 'bg-amber-500/80 text-white' :
              artifact.type === 'remedy' ? 'bg-rose-500/80 text-white' :
              artifact.type === 'video' ? 'bg-blue-500/80 text-white' :
              'bg-green-500/80 text-white'
            }`}>
              {artifact.type === 'video' && <Play size={10} className="inline mr-1" />}
              {artifact.type.charAt(0).toUpperCase() + artifact.type.slice(1)}
            </span>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-semibold text-stone-800 mb-1 text-left line-clamp-1">
              {artifact.title}
            </h3>
            <p className="text-xs text-stone-600 mb-3 text-left line-clamp-2">
              {artifact.description}
            </p>
            
            {/* Meta */}
            <div className="flex items-center justify-between text-xs text-stone-500">
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {artifact.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <Heart size={12} className="text-rose-400" />
                  {artifact.stats.loves}
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare size={12} className="text-blue-400" />
                  {artifact.stats.comments}
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

// Timeline View Component
const TimelineView = ({ artifacts, onArtifactClick }) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-200 via-amber-200 to-purple-200"></div>

        {/* Timeline Items */}
        <div className="space-y-8">
          {artifacts.map((artifact, index) => (
            <div key={artifact.id} className="relative flex gap-6 group">
              {/* Date Dot */}
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-white border-4 border-purple-200 flex items-center justify-center shadow-lg z-10">
                  <span className="text-xs font-bold text-stone-700">
                    {artifact.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Content Card */}
              <button
                onClick={() => onArtifactClick(artifact)}
                className="flex-1 bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left"
              >
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  <div className="w-32 h-32 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                    <img 
                      src={artifact.image} 
                      alt={artifact.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-stone-800">
                        {artifact.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        artifact.type === 'moment' ? 'bg-purple-100 text-purple-700' :
                        artifact.type === 'decision' ? 'bg-amber-100 text-amber-700' :
                        artifact.type === 'remedy' ? 'bg-rose-100 text-rose-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {artifact.type}
                      </span>
                    </div>
                    
                    <p className="text-sm text-stone-600 mb-3 line-clamp-2">
                      {artifact.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {artifact.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-stone-500">
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        {artifact.participants.length} people
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart size={12} className="text-rose-400" />
                        {artifact.stats.loves}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare size={12} className="text-blue-400" />
                        {artifact.stats.comments}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Story Mode View Component
const StoryView = ({ artifacts, currentSlide, isPlaying, onPlay, onPause, onPrev, onNext }) => {
  if (artifacts.length === 0) {
    return <div className="text-center text-stone-500 py-20">No artifacts to display</div>;
  }

  const artifact = artifacts[currentSlide];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
        {/* Main Image */}
        <div className="relative h-[500px] bg-stone-900">
          <img 
            src={artifact.image} 
            alt={artifact.title}
            className="w-full h-full object-cover"
          />
          
          {/* Navigation Overlays */}
          <button
            onClick={onPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all"
          >
            <ChevronRight size={24} />
          </button>

          {/* Play/Pause Control */}
          <button
            onClick={isPlaying ? onPause : onPlay}
            className="absolute bottom-4 right-4 p-3 bg-purple-500/90 hover:bg-purple-600 rounded-full text-white transition-all"
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>

          {/* Progress Indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <div 
              className="h-full bg-purple-500 transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / artifacts.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Story Content */}
        <div className="p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium mb-3 ${
                artifact.type === 'moment' ? 'bg-purple-100 text-purple-700' :
                artifact.type === 'decision' ? 'bg-amber-100 text-amber-700' :
                artifact.type === 'remedy' ? 'bg-rose-100 text-rose-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {artifact.type.charAt(0).toUpperCase() + artifact.type.slice(1)}
              </span>
              <h2 className="text-3xl font-bold text-stone-800 mb-2">
                {artifact.title}
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed">
                {artifact.description}
              </p>
            </div>
            <span className="text-sm text-stone-500">
              {currentSlide + 1} / {artifacts.length}
            </span>
          </div>

          {artifact.remedyQuote && (
            <div className="bg-rose-50 border-l-4 border-rose-400 p-4 mb-4 italic text-stone-700">
              {artifact.remedyQuote}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center justify-between pt-6 border-t border-stone-200">
            <div className="flex items-center gap-6 text-sm text-stone-600">
              <span className="flex items-center gap-2">
                <Clock size={16} />
                {artifact.date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-2">
                <Users size={16} />
                {artifact.participants.join(', ')}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <button className="flex items-center gap-2 px-4 py-2 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-lg transition-colors">
                <Heart size={16} />
                {artifact.stats.loves}
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors">
                <MessageSquare size={16} />
                {artifact.stats.comments}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Navigation */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        {artifacts.map((art, index) => (
          <button
            key={art.id}
            onClick={() => {}}
            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
              index === currentSlide 
                ? 'border-purple-500 scale-110' 
                : 'border-transparent opacity-50 hover:opacity-100'
            }`}
          >
            <img src={art.image} alt={art.title} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Masonry View Component
const MasonryView = ({ artifacts, onArtifactClick }) => {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
      {artifacts.map((artifact, index) => (
        <button
          key={artifact.id}
          onClick={() => onArtifactClick(artifact)}
          className="group break-inside-avoid bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full"
        >
          {/* Image */}
          <div className="w-full overflow-hidden bg-stone-100" style={{ height: `${200 + (index % 3) * 100}px` }}>
            <img 
              src={artifact.image} 
              alt={artifact.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                artifact.type === 'moment' ? 'bg-purple-100 text-purple-700' :
                artifact.type === 'decision' ? 'bg-amber-100 text-amber-700' :
                artifact.type === 'remedy' ? 'bg-rose-100 text-rose-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {artifact.type}
              </span>
              <span className="text-xs text-stone-500">
                {artifact.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
            
            <h3 className="font-semibold text-stone-800 mb-2 text-left">
              {artifact.title}
            </h3>
            <p className="text-xs text-stone-600 mb-3 text-left">
              {artifact.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-1 mb-3">
              {artifact.tags.slice(0, 3).map(tag => (
                <span key={tag} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-xs text-stone-500">
              <span className="flex items-center gap-1">
                <Users size={12} />
                {artifact.participants.length}
              </span>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <Heart size={12} className="text-rose-400" />
                  {artifact.stats.loves}
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare size={12} className="text-blue-400" />
                  {artifact.stats.comments}
                </span>
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

// Artifact Detail Modal
const ArtifactDetailModal = ({ artifact, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-stone-800">{artifact.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Image */}
        <div className="w-full h-96 bg-stone-900">
          <img 
            src={artifact.image} 
            alt={artifact.title}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
              artifact.type === 'moment' ? 'bg-purple-100 text-purple-700' :
              artifact.type === 'decision' ? 'bg-amber-100 text-amber-700' :
              artifact.type === 'remedy' ? 'bg-rose-100 text-rose-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {artifact.type.charAt(0).toUpperCase() + artifact.type.slice(1)}
            </span>
            <span className="text-sm text-stone-500">
              {artifact.date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>

          <p className="text-stone-700 text-lg leading-relaxed mb-6">
            {artifact.description}
          </p>

          {artifact.remedyQuote && (
            <div className="bg-rose-50 border-l-4 border-rose-400 p-4 mb-6 italic text-stone-700">
              {artifact.remedyQuote}
            </div>
          )}

          {/* Participants */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
              <Users size={16} />
              Participants
            </h3>
            <div className="flex flex-wrap gap-2">
              {artifact.participants.map(person => (
                <span key={person} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                  {person}
                </span>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
              <Tag size={16} />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {artifact.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-stone-100 text-stone-600 text-sm rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 pt-6 border-t border-stone-200">
            <button className="flex items-center gap-2 px-6 py-3 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-xl transition-colors font-medium">
              <Heart size={20} />
              {artifact.stats.loves} Loves
            </button>
            <button className="flex items-center gap-2 px-6 py-3 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors font-medium">
              <MessageSquare size={20} />
              {artifact.stats.comments} Comments
            </button>
            <button className="flex items-center gap-2 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl transition-colors font-medium ml-auto">
              <Download size={20} />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EpicLoreLibrary;
